// DocViewer — the read surface for one doc: header (name, path, actions),
// the provenance line derived from revision rows, and the markdown body
// rendered by `ChatMarkdown` so internal plant/graph links and `cite:` tokens
// resolve exactly as they do in chat.
//
// "Update with agent" hands the turn to the chat sidebar: the query agent owns
// docs, and the revision history + diff + Restore in the history panel are the
// review surface for whatever it writes.
//
// "Export as" offers Markdown (client-side blob of the loaded content) plus
// DOCX and PDF (BFF `/api/ontology/docs/:id/export` — the UI never imports
// the authoring package).

import { useState } from 'react'
import { ChevronDown, Download, FileText, History, Pencil, Sparkles } from 'lucide-react'
import { toast } from 'sonner'
import {
  ingestMetaFromDoc,
  type Doc,
  type DocRevisionMeta
} from '@axilon-apps/domains-ontology-ui-state'

import { ChatMarkdown } from '../../components/ChatMarkdown/ChatMarkdown'
import IngestionDetails from './IngestionDetails'
import { Button } from '../../components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../../components/ui/dropdown-menu'
import {
  buildProvenanceLine,
  clockTime,
  describeLease,
  downloadDocExport,
  type DocsExportFormat
} from './doc-helpers'

export interface DocViewerProps {
  doc: Doc
  revisions: readonly DocRevisionMeta[]
  hasMoreRevisions: boolean
  historyOpen: boolean
  canEdit: boolean
  onEdit: () => void
  onUpdateWithAgent: () => void
  onToggleHistory: () => void
}

const DocViewer = ({
  doc,
  revisions,
  hasMoreRevisions,
  historyOpen,
  canEdit,
  onEdit,
  onUpdateWithAgent,
  onToggleHistory
}: DocViewerProps) => {
  const [exporting, setExporting] = useState(false)
  const ingestMeta = ingestMetaFromDoc(doc)
  const lease = describeLease(doc)
  const holderLabel = lease.holderKind === 'agent' ? `Agent ${lease.holderName}` : lease.holderName
  const editLabel =
    lease.state === 'held'
      ? `${holderLabel} is editing`
      : lease.state === 'expired'
        ? 'Take over editing'
        : 'Edit'
  const editHint =
    lease.state === 'held'
      ? `${holderLabel} holds the edit lock until ${clockTime(lease.expiresAt)} — you can take over once it expires.`
      : lease.state === 'expired'
        ? `${holderLabel}'s edit lock expired at ${clockTime(lease.expiresAt)}.`
        : undefined

  const handleExport = (format: DocsExportFormat) => {
    if (exporting) return
    setExporting(true)
    void downloadDocExport({ format, doc, revisions })
      .then((result) => {
        if (result.method === 'print') {
          toast('Opening print dialog for PDF', {
            description: 'Choose “Save as PDF” in the system print dialog.'
          })
        }
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : String(err)
        toast.error(message || 'Export failed')
      })
      .finally(() => setExporting(false))
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <header className="flex shrink-0 flex-col gap-2 border-b border-line px-4 py-2.5">
        <div className="flex items-center gap-3">
          <FileText className="size-4 shrink-0 text-ink-3" aria-hidden />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-ink">{doc.name}</div>
            <div className="truncate font-mono text-11 text-ink-3">/{doc.path}</div>
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={!canEdit || lease.state === 'held'}
            title={editHint}
            onClick={onEdit}
          >
            <Pencil aria-hidden />
            {editLabel}
          </Button>
          <Button size="sm" variant="outline" onClick={onUpdateWithAgent}>
            <Sparkles aria-hidden />
            Update with agent
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" disabled={exporting} aria-label="Export as">
                <Download aria-hidden />
                Export as
                <ChevronDown aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-28">
              <DropdownMenuItem
                className="py-1 text-xs"
                disabled={exporting}
                onSelect={() => handleExport('md')}
              >
                Markdown
              </DropdownMenuItem>
              <DropdownMenuItem
                className="py-1 text-xs"
                disabled={exporting}
                onSelect={() => handleExport('docx')}
              >
                DOCX
              </DropdownMenuItem>
              <DropdownMenuItem
                className="py-1 text-xs"
                disabled={exporting}
                onSelect={() => handleExport('pdf')}
              >
                PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            size="icon-sm"
            variant={historyOpen ? 'default' : 'ghost'}
            aria-label="Version history"
            aria-pressed={historyOpen}
            title="Version history"
            onClick={onToggleHistory}
          >
            <History aria-hidden />
          </Button>
        </div>
        <div className="flex items-center gap-1.5 text-11 text-ink-3">
          <span>{buildProvenanceLine(revisions, { hasMore: hasMoreRevisions })}</span>
          {revisions.length > 0 && !historyOpen ? (
            <button
              type="button"
              className="cursor-pointer border-0 bg-transparent p-0 text-11 text-primary underline-offset-2 hover:underline"
              onClick={onToggleHistory}
            >
              View history
            </button>
          ) : null}
        </div>
      </header>
      <div className="min-h-0 flex-1 overflow-auto px-6 py-5">
        {doc.content.trim() === '' ? (
          <p className="m-0 text-sm text-ink-3">
            This doc is empty. Edit it, or ask the agent to draft it from the ontology.
          </p>
        ) : (
          <ChatMarkdown className="max-w-[76ch] text-sm text-ink-2">{doc.content}</ChatMarkdown>
        )}
        {ingestMeta === null ? null : (
          <div className="mt-8 max-w-[40rem] border-t border-line pt-4">
            <IngestionDetails meta={ingestMeta} />
          </div>
        )}
      </div>
    </div>
  )
}

export default DocViewer
